/**
 * ============================================================================
 * ⭐⭐ A PARTY'S LOADS FOR THE DAY — ONE PREDICATE, USED BY BOTH SIDES
 * ============================================================================
 *
 * Decision 188, increments 3 and 5 (§1AV.171, §1AV.173).
 *
 * ⭐ Pete, 2026-09-19: *"clicking on client, consignee and carrier do nothing yet"*.
 * This is what makes them do something: the panel lists PARTIES instead of drivers,
 * and choosing one scopes the rail AND the calendar.
 *
 * ---- ⛔⛔ WHY `apptMatchesParty` IS EXPORTED AND USED TWICE -------------------
 *
 * The card says *"Hellman · 4 loads"* and the grid then draws Hellman's loads. Those
 * are two answers to the same question, and if they are computed by two expressions
 * they will disagree the first time either is refined — a card promising four loads
 * over a grid showing three is a bug nobody can diagnose from the screen.
 *
 *   ** A COUNT AND A FILTER THAT MUST AGREE ARE ONE PREDICATE CALLED TWICE, NEVER
 *      TWO PREDICATES THAT CURRENTLY MATCH. **
 *
 * ⚠️ §1AV.170 has this exact fault recorded for `StatusFilterBar`, where the count
 * and the row were built separately and drifted for a day and a half.
 *
 * ---- ⛔⛔ CONSIGNEE IS A JOIN, AND THE COUNTS DO NOT SUM ---------------------
 *
 * `clientCode` and `carrierCode` are scalar columns; `consignees` is an ARRAY on the
 * appointment (`dock.AppointmentConsignee`). So one load legitimately appears under
 * several consignees, and the per-consignee counts **deliberately do not add up** to
 * the day's truck count. ⛔ That is not a rounding error to hide — decision 21 says
 * the panel must say so on screen.
 */
import type { AppointmentDto } from '@/api/types';
import type { PanelSubject } from '@/lib/panelSubject';

export type PartyKind = 'client' | 'carrier' | 'consignee';

/** ⛔ The party kinds, as a value — `PANEL_SUBJECTS` minus `driver`. */
export const PARTY_KINDS: readonly PartyKind[] = ['client', 'carrier', 'consignee'] as const;

/**
 * ⛔⛔ THIS IS WHAT KEEPS A HOVER FROM FILTERING THE WHOLE CALENDAR.
 *
 * `RailFocus.kind` has five values and only three of them are parties. Dwelling on a
 * driver card latches a `driver` focus; if the grid filtered on every focus, moving
 * the mouse across the rail would empty the schedule.
 *
 *   ** A TYPE THAT CARRIES FIVE CASES INTO A PLACE THAT HANDLES THREE NEEDS THE TEST
 *      AT THE BOUNDARY, NOT A COMMENT ASKING CALLERS TO BE CAREFUL. **
 */
export function isPartyKind(kind: string): kind is PartyKind {
    return kind === 'client' || kind === 'carrier' || kind === 'consignee';
}

/** ⛔ `driver` is not a party — it returns null rather than a fourth kind. */
export function partyKindFor(subject: PanelSubject): PartyKind | null {
    return isPartyKind(subject) ? subject : null;
}

/**
 * ⛔⛔ THE ONE PREDICATE. Both the card counts and `filterAppts` call this.
 *
 * ⚠️ `direction === 'BLOCK'` IS EXCLUDED FIRST. A block is a door outage, not a
 * load; it carries a status code and would otherwise fall through the party test on
 * whatever empty string its client column holds — putting every facility's blocks
 * under one phantom party.
 */
export function apptMatchesParty(a: AppointmentDto, kind: PartyKind, code: string): boolean {
    if (a.direction === 'BLOCK') return false;
    if (kind === 'client')  return a.clientCode === code;
    if (kind === 'carrier') return a.carrierCode === code;
    return a.consignees.some(c => c.consigneeCode === code);
}

export interface PartyRow {
    kind: PartyKind;
    code: string;
    /** The display name, falling back to the code — never an empty string. */
    name: string;
    appointments: AppointmentDto[];
    /** ⭐ Decision 19: the three buckets, from the statuses already on the status bar. */
    arrived: number;
    departed: number;
    remaining: number;
    /** ⭐ Decision 18 — warning glyph #1: appointments at risk right now. */
    atRisk: number;
    /**
     * ---- ⭐⭐⭐ WHICH LOADS, NOT JUST HOW MANY (Pete, 2026-09-23) ---------------
     *
     * > *"when you hover over them ... they need to highlight the appointmets with the
     * > warnings and then when you click on the warning, it only shows the appointments
     * > with the warnings in the calendar."*
     *
     * ⛔⛔ THE COUNT COULD NOT ANSWER THAT AND NEITHER COULD `appointments`. The card
     * knew *"3 loads overdue"* and it knew the party's **whole** list; nothing anywhere
     * held the THREE. A hover built from `appointments` would have highlighted all
     * eleven — the right gesture pointing at the wrong set, which reads as a broken
     * highlight rather than as a missing filter.
     *
     * ⭐⭐ AND THEY ARE BUILT FROM THE SAME `filter` THAT PRODUCES THE COUNT, not from
     * a second predicate written beside it. `atRisk` is now `atRiskIds.length` — so the
     * sentence on the card and the loads that light up on the board are the same answer
     * read twice, exactly as `apptMatchesParty` is for the card and the grid.
     *   ** A COUNT AND THE SET IT COUNTS MUST NOT BE TWO EXPRESSIONS. MAKE THE COUNT
     *      THE SET'S LENGTH AND THEY CANNOT DISAGREE. **
     *
     * ⚠️ STRINGS, because `RailFocus.appointmentIds` is `string[]` and every matcher
     * downstream compares `String(a.appointmentId)`. Converting here rather than at four
     * call sites is the same argument §1AV.170 makes about `StatusFilterBar`.
     */
    atRiskIds: string[];
    /**
     * ⭐ Decision 24 — warning glyph #2, PARTIALLY. `paperworkCount` tells us whether
     * anything is filed. ⛔ "UNSIGNED" IS NOT IN THE PAYLOAD and is not inferred here:
     * `AppointmentDto` carries a count, not a signature state.
     *   ** A GLYPH THAT CLAIMS MORE THAN THE DATA SUPPORTS IS WORSE THAN A MISSING
     *      GLYPH, BECAUSE IT WILL BE TRUSTED. ** The unsigned half is owed.
     * ⚠️ `paperworkCount` is optional — an older or degraded payload leaves it
     * undefined, which must read as "unknown", not as "nothing filed".
     */
    noPaperwork: number;
    /** ⭐ The loads behind `noPaperwork` — see `atRiskIds`. */
    noPaperworkIds: string[];
    /**
     * ---- ⭐⭐⭐ LABOUR, ON THE PARTY CARD TOO (Pete, 2026-09-24) ---------------
     *
     * > *"We need to show on the carrier/client/consignee that labor is in progress
     * > too, just like the driver panel."* → *"Same as on the driver card -
     * > consistency."*
     *
     * ⛔⛔ THE CARD ALREADY HAD A NUMBER CALLED "in progress" AND IT MEANT ARRIVED.
     * `arrived` counts `statusCode === 'ARRIVED'` — through the gate — which says
     * nothing about whether anybody is on the truck. A second, real "in progress"
     * landing beside it would have made one phrase on one card mean two things, so
     * the older line now says **on site**, the word `statusWord` already uses for
     * ARRIVED on the driver card.
     *   ** WHEN A NEW FACT ARRIVES WEARING AN OLD FACT'S WORD, RENAME THE OLD ONE.
     *      TWO MEANINGS FOR ONE PHRASE ON ONE CARD IS NOT A LABEL PROBLEM, IT IS A
     *      NUMBER NOBODY CAN RECONCILE. **
     *
     * ⭐ SAME PRECEDENCE AS `workStatusLabel`: active beats paused. A load counted
     * in both would make `working + paused` exceed the trucks on the card.
     *
     * ⚠️ AND `paused` IS CARRIED, NOT JUST `working`. `laborActive` is
     * `State = 'Active'`, so it goes false the moment somebody presses Pause — a
     * card that drew only `working` would go blank on a party whose every load is
     * paused, which is exactly the fault `PauseMark.tsx` exists to record:
     *   ** ABSENCE IS NOT A STATE. A READER CANNOT TELL "NOBODY EVER STARTED" FROM
     *      "SOMEBODY STOPPED". **
     *
     * ⚠️ `laborDone` IS DELIBERATELY NOT COUNTED HERE. `departed` already answers
     * "finished with", and a third completion number on a four-number row buys less
     * than it costs. ⬜ Recorded rather than faked, like the unsigned half above.
     */
    working: number;
    /** ⭐ The loads behind `working` — see `atRiskIds`. */
    workingIds: string[];
    paused: number;
    /** ⭐ The loads behind `paused` — see `atRiskIds`. */
    pausedIds: string[];
}

/** Statuses that mean the truck has not been dealt with yet. */
const REMAINING = new Set(['REQUESTED', 'APPROVED']);

/**
 * ⭐ AT RISK = something a scheduler would act on before lunch.
 *
 * ⛔ NOT "anything unusual". A cancelled load is settled; a departed one is done.
 * Risk is: it was marked no-show, or its slot has passed and nobody has arrived.
 * ⚠️ `now` IS A PARAMETER, not `new Date()` inside. A function that reads the clock
 * cannot be tested for the boundary it exists to detect.
 */
function isAtRisk(a: AppointmentDto, nowMs: number): boolean {
    if (a.statusCode === 'NOSHOW') return true;
    if (!REMAINING.has(a.statusCode)) return false;
    return Date.parse(a.scheduledStartUtc) < nowMs;
}

/**
 * Build one row per party present in `appts`, sorted by name.
 *
 * ⛔ AN APPOINTMENT WITH NO CODE IS SKIPPED, NOT BUCKETED UNDER ''. An empty client
 * code is "we do not know yet", and a row labelled with the empty string invites
 * somebody to select it and scope the whole calendar to nothing.
 */
/**
 * ---- ⛔⛔ THE NAME ON A LOAD IS NOT THE PARTY'S NAME (2026-09-19) -------------
 *
 * ⭐ Pete: *"Not giving me the full name GEODIS for example on the cards - are you
 * taking the name or the code?"*
 *
 * ⚠️ MEASURED: `AppointmentDto.carrierName` / `.clientName` come from
 * `dock.Appointment.CarrierName` / `.ClientName` — values COPIED ONTO THE BOOKING at
 * creation time, and nullable. GEODIS's bookings have it blank, so the card fell
 * back to the code `GEOT`; FedEx's have it filled, so FedEx read correctly.
 *
 * ⛔⛔ AND THE REAL FAULT IS WORSE THAN A BLANK. Because that name lives on the
 * APPOINTMENT rather than on the party, two bookings for one carrier may carry
 * different spellings — or one may be null — and the card's label then depends on
 * which appointment the loop met first.
 *
 *   ** A NAME STORED ON EACH TRANSACTION IS NOT A NAME, IT IS A COPY PER
 *      TRANSACTION. AGGREGATING BY IT MAKES THE LABEL DEPEND ON ITERATION ORDER. **
 *
 * ✅ SO A MASTER LOOKUP WINS WHEN THERE IS ONE. `dock.Carrier` and
 * `dock.vw_KnownClients` already reach the page in `lookups` — they were being
 * fetched and used only for dropdowns. ⛔ Order: MASTER, then the booking's copy,
 * then the code. Never blank.
 *
 * ⚠️ CONSIGNEES HAVE NO MASTER IN THAT PAYLOAD. `dock.Consignee` is a real table but
 * is not in `getLookups`, so consignee rows still read the name on the load. ⬜ That
 * is a backend change, recorded rather than faked — a resolver that silently returns
 * the code for one axis while resolving the other two is the kind of asymmetry that
 * gets diagnosed as "the consignee data is bad".
 */
export function partyNameKey(kind: PartyKind, code: string): string {
    return `${kind}:${code}`;
}

/**
 * ---- ⭐⭐ DOES **ANY** CARD OF THIS KIND ACCOUNT FOR THIS LOAD? (Pete, 2026-09-20) ----
 *
 * ⭐ Pete, on the Consignees chip showing an empty card list beside a full board:
 * *"It should not be showing any calender appts here because no consignees are on these
 * appts. Panels (all) should be blank for all panels."*
 *
 * ⭐⭐⭐ ONE RULE, THREE OUTCOMES — which is why it is not a special case for consignee.
 * Every load carries a client, so the Clients chip shows the whole board. Almost none
 * carries a consignee, so the Consignees chip shows nothing. Carrier sits between, and
 * `TBD` counts as a carrier because it IS the stored value (§1R.8a).
 *   ** A CHIP SHOWS WHAT ITS CARDS ACCOUNT FOR. AN EMPTY CARD LIST BESIDE A FULL BOARD
 *      IS THE SCREEN CONTRADICTING ITSELF. **
 *
 * ⛔ THE SAME EMPTINESS TEST `partyRows`' OWN `add()` USES (`if (!code) return`), so the
 * board and the card list cannot disagree about what counts as present.
 * ⚠️ BLOCKS ARE NOT LOADS and are excluded here exactly as they are in `partyRows` —
 * a door outage belongs to the building, not to a party. The CALLER re-admits them.
 */
/**
 * ---- ⛔⛔ `TBD` IS A SENTINEL, NOT A COMPANY (Pete, 2026-09-20) --------------------
 *
 * ⭐ Pete, on a card reading `TBD · Trucks 13`: *"Why are we setting carrier to TBD and
 * not blank when saving the appointment - it says its optional and should be no carrier
 * set, not TBD."*
 *
 * ✅ THE STORED VALUE IS RIGHT AND STAYS. §1R.8a: the server writes `'TBD'` at BOTH the
 * create and edit sites so *"an edit must not be able to blank it"*, and it is **119 of
 * 290 appointments — 41% of the estate**, exactly what §1R.8 predicts a brokered-load
 * estate looks like. A blank would make "nobody has told us yet" and "somebody cleared
 * this by accident" the same state.
 *
 * ⛔⛔ WHAT WAS NEW IS THE **RENDERING**. Nothing had ever displayed carriers as CARDS
 * before 2026-09-19, so no one had decided whether an absence-marker deserves one. It
 * does — those thirteen trucks are real and a scheduler must see them — but it must
 * stop wearing a company's clothes.
 *   ** A SENTINEL IS SAFE UNTIL SOMETHING RENDERS IT AS A NOUN. **
 *
 * ⚠️ DISPLAY ONLY. `code` is untouched, so the focus, the dropdown value, the matcher
 * and everything that reaches the server still say `TBD`. ⛔ Rewriting the CODE here
 * would be §1R.8a's defect inverted — a second sentinel nobody else knows about.
 */
export function displayPartyName(kind: PartyKind, code: string, name: string | null): string {
    if (kind === 'carrier' && code.trim().toUpperCase() === 'TBD') return 'No carrier yet';
    return name?.trim() || code;
}

export function apptHasPartyKind(a: AppointmentDto, kind: PartyKind): boolean {
    if (a.direction === 'BLOCK') return false;
    if (kind === 'client')  return !!a.clientCode?.trim();
    if (kind === 'carrier') return !!a.carrierCode?.trim();
    return a.consignees.some(c => !!c.consigneeCode?.trim());
}

export function partyRows(
    kind: PartyKind,
    appts: readonly AppointmentDto[],
    nowMs: number,
    /** Master names, keyed by `partyNameKey`. Absent entries fall through. */
    names?: ReadonlyMap<string, string>,
): PartyRow[] {
    const byCode = new Map<string, { name: string; appts: AppointmentDto[] }>();

    const add = (code: string, name: string | null, a: AppointmentDto) => {
        if (!code) return;
        const hit = byCode.get(code);
        if (hit) { hit.appts.push(a); if (!hit.name && name) hit.name = name.trim(); return; }
        /* ⛔ MASTER FIRST, AND IT IS RESOLVED ONCE PER PARTY RATHER THAN PER LOAD —
           which is precisely what stops the label depending on iteration order. */
        const master = names?.get(partyNameKey(kind, code));
        byCode.set(code, { name: displayPartyName(kind, code, master?.trim() || name?.trim() || null), appts: [a] });
    };

    for (const a of appts) {
        if (a.direction === 'BLOCK') continue;
        if (kind === 'client')  { add(a.clientCode,  a.clientName,  a); continue; }
        if (kind === 'carrier') { add(a.carrierCode, a.carrierName, a); continue; }
        /* ⛔ EVERY consignee on the load, not the first. A load bound for three
           consignees belongs on all three cards — that is what makes the counts
           legitimately exceed the truck total. */
        for (const c of a.consignees) add(c.consigneeCode, c.consigneeName, a);
    }

    return [...byCode.entries()]
        .map(([code, v]) => {
        /* ⭐ RESOLVED ONCE, USED TWICE — the list and its length. ⛔ Not inlined into
           the object literal: an expression that has to appear in two properties is the
           shape that drifts, which is this module's opening argument. */
        const atRiskIds = v.appts.filter(a => isAtRisk(a, nowMs)).map(a => String(a.appointmentId));
        const noPaperworkIds = v.appts.filter(a => a.paperworkCount === 0).map(a => String(a.appointmentId));
        /* ⭐ ACTIVE BEATS PAUSED, and the two lists are therefore DISJOINT — the same
           precedence `labourState.workStatusLabel` uses, written the same way round.
           ⛔ Not `a.laborPaused` alone: a backend that ever set both would otherwise
           have one appointment counted twice and the card's numbers would exceed its trucks. */
        const workingIds = v.appts.filter(a => a.laborActive).map(a => String(a.appointmentId));
        const pausedIds  = v.appts.filter(a => !a.laborActive && a.laborPaused === true)
                                  .map(a => String(a.appointmentId));
        return {
            kind,
            code,
            name: v.name || code,
            appointments: v.appts,
            arrived:   v.appts.filter(a => a.statusCode === 'ARRIVED').length,
            departed:  v.appts.filter(a => a.statusCode === 'DEPARTED').length,
            remaining: v.appts.filter(a => REMAINING.has(a.statusCode)).length,
            /* ⛔⛤ ~~`.filter(...).length` TWICE~~ — 2026-09-23. The two warnings now
               carry their appointments (see `atRiskIds`), and the counts are the LENGTHS of
               those lists rather than a second pass over the same predicate. ⚠️ The
               predicates are unchanged, deliberately: this moves where the answer is
               stored, not what counts as at-risk. */
            atRisk:    atRiskIds.length,
            atRiskIds,
            /* ⚠️ `=== 0`, NOT `!a.paperworkCount` — undefined means "the payload did
               not say", and counting that as "nothing filed" would put a warning on
               every appointment the moment the side-load degrades. */
            noPaperwork: noPaperworkIds.length,
            noPaperworkIds,
            /* ⚠️ `=== true` ON `laborPaused`, NOT truthiness — it is optional on the
               DTO, and `undefined` means a degraded payload did not say, which must
               not render as "nobody is on it". §1AV: A NEW FIELD'S ABSENCE MUST MEAN
               "I DO NOT KNOW". (`laborActive` is required, so it needs no such test.) */
            working: workingIds.length,
            workingIds,
            paused: pausedIds.length,
            pausedIds,
        };
        })
        /* ⭐ BY NAME, NOT BY COUNT. A list that reorders itself as trucks arrive is a
           list you cannot learn the shape of; the scheduler is looking for a
           particular client, not the busiest one. */
        .sort((x, y) => x.name.localeCompare(y.name));
}
